import { typeRampBaseFontSize, typeRampBaseLineHeight, typeRampBaseFontVariations, typeRampMinus1FontSize, typeRampMinus1LineHeight, typeRampMinus2FontSize, typeRampMinus2LineHeight, typeRampPlus1FontSize, typeRampPlus1LineHeight, typeRampPlus2FontSize, typeRampPlus2LineHeight, typeRampPlus3FontSize, typeRampPlus3LineHeight, typeRampPlus4FontSize, typeRampPlus4LineHeight, typeRampPlus5FontSize, typeRampPlus5LineHeight, typeRampPlus6FontSize, typeRampPlus6LineHeight } from '@adaptive-web/adaptive-ui/reference';
import { css } from '@microsoft/fast-element';
import { DesignToken, type DesignTokenResolver } from '@microsoft/fast-foundation';

function setTypeRamp(resolve: DesignTokenResolver, base: string, invert: boolean = false): string {
	const ratio = resolve(typeRampRatio);

	return `calc(${base} ${invert ? '/' : '*'} ${ratio})`;
}

export const typeRampRatio = DesignToken.create<number>('type-ramp-ratio').withDefault(1.125);
export const lineHeightRatio = DesignToken.create<number>('line-height-ratio').withDefault(1.2);

typeRampBaseFontSize.withDefault('1rem');
typeRampBaseLineHeight.withDefault('1rlh');
typeRampBaseFontVariations.withDefault('"wght" 400');

typeRampMinus2FontSize.withDefault(resolve => {
	const base = resolve(typeRampMinus1FontSize);
	return setTypeRamp(resolve, base, true);
});

typeRampMinus1FontSize.withDefault(resolve => {
	const base = resolve(typeRampBaseFontSize);
	return setTypeRamp(resolve, base, true);
});

typeRampPlus1FontSize.withDefault(resolve => {
  const base = resolve(typeRampBaseFontSize);
  return setTypeRamp(resolve, base);
});

typeRampPlus2FontSize.withDefault(resolve => {
	const base = resolve(typeRampPlus1FontSize);
	return setTypeRamp(resolve, base);
});

typeRampPlus3FontSize.withDefault(resolve => {
	const base = resolve(typeRampPlus2FontSize);
	return setTypeRamp(resolve, base);
});

typeRampPlus4FontSize.withDefault(resolve => {
	const base = resolve(typeRampPlus3FontSize);
	return setTypeRamp(resolve, base);
});

typeRampPlus5FontSize.withDefault(resolve => {
	const base = resolve(typeRampPlus4FontSize);
	return setTypeRamp(resolve, base);
});

typeRampPlus6FontSize.withDefault(resolve => {
	const base = resolve(typeRampPlus5FontSize);
	return setTypeRamp(resolve, base);
});

export const typeRampStyles = css`
	:is(h1, h2, h3, h4, h5, h6, p) {
		margin: 0;
	}

	:is(h2) {
		--font-variations: 'wght' 600;
		font-size: 28px;
	}

	:is(h3) {
		font-size: 22px;
	}

	:is(h3) {
		font-size: 22px;
	}

	:where(sub, sup) {
		--font-size: ${typeRampMinus2FontSize};
		--font-variations: ${typeRampBaseFontVariations};
		font-size: var(--font-size);
		line-height: var(--line-height);
		font-variation-settings: var(--font-variations);
	}

	:where(small) {
		--font-size: ${typeRampMinus1FontSize};
		--font-variations: ${typeRampBaseFontVariations};
		font-size: var(--font-size);
		line-height: var(--line-height);
		font-variation-settings: var(--font-variations);
	}

	:where(p) {
		--font-size: ${typeRampBaseFontSize};
		--line-height: 1.2rlh;
		--font-variations: ${typeRampBaseFontVariations};
		font-size: var(--font-size);
		line-height: var(--line-height);
		font-variation-settings: var(--font-variations);
	}

	:where(h6) {
		--font-size: ${typeRampPlus1FontSize};
		--font-variations: ${typeRampBaseFontVariations};
		font-size: var(--font-size);
		line-height: var(--line-height);
		font-variation-settings: var(--font-variations);
	}

	:where(h5) {
		--font-size: ${typeRampPlus2FontSize};
		--font-variations: ${typeRampBaseFontVariations};
		font-size: var(--font-size);
		line-height: var(--line-height);
		font-variation-settings: var(--font-variations);
	}

	:where(h4) {
		--font-size: ${typeRampPlus3FontSize};
		--font-variations: ${typeRampBaseFontVariations};
		font-size: var(--font-size);
		line-height: var(--line-height);
		font-variation-settings: var(--font-variations);
	}

	:where(h3) {
		--font-size: ${typeRampPlus4FontSize};
		--font-variations: ${typeRampBaseFontVariations};
		font-size: var(--font-size);
		line-height: var(--line-height);
		font-variation-settings: var(--font-variations);
	}

	:where(h2) {
		--font-size: ${typeRampPlus5FontSize};
		--font-variations: ${typeRampBaseFontVariations};
		font-size: var(--font-size);
		line-height: var(--line-height);
		font-variation-settings: var(--font-variations);
	}

	:where(h1) {
		--font-size: ${typeRampPlus6FontSize};
		--font-variations: ${typeRampBaseFontVariations};
		font-size: var(--font-size);
		line-height: var(--line-height);
		font-variation-settings: var(--font-variations);
	}

	@supports (font-size: 1cqi) {
		:is(.fluid-type) {
			--font-min: var(--font-size) - var(--font-size) * var(--font-size-diff, 0.3);
			--line-min: var(--line-height, 1.2rlh) - var(--line-height, 1.2rlh) * var(--line-height-diff, -0.1);

			font-size: clamp(
				max(${typeRampMinus2FontSize}, var(--font-min)),
				var(--font-min) + 1cqi,
				var(--font-size)
			);

			line-height: clamp(
				max(${typeRampMinus2LineHeight}, var(--line-min)),
				var(--line-min) + 1cqi,
				var(--line-height, 1.2rlh)
			);

			font-variation-settings: var(--font-variations);
		}
	}
`;